import { createElement } from 'react';

import { cn } from '../../utils/cn';
import type { ViewProps } from './View.types';

/**
 * Web View — renders a `<div>` (or chosen `as` element) styled to match
 * react-native's View defaults: column flex with stretch alignment, box-border,
 * and no implicit min-content sizing. The same className then produces the
 * same layout result on both platforms.
 *
 * ── `min-h-0`/`min-w-0` and `fk-view` ship TOGETHER. Do not split them. ──
 *
 * RN/Yoga flex items have `flexShrink: 0`, and no min-content floor.
 * react-native-web's own View reset encodes BOTH halves; this reset shipped only
 * the min-* half, so on web every shared View was an infinitely shrinkable flex
 * item — inside any height-capped flex column it collapsed to its padding
 * instead of letting the column scroll. That is what crushed the deck Study
 * plan list (rows squashed 96px → 32px and overlapped) and the plan dialog's
 * fields on a short viewport.
 *
 * The no-shrink default is a BASE-LAYER rule on `.fk-view`, emitted by the shared
 * Tailwind preset (`../../styles/tailwind-preset.cjs`) so that every web host
 * gets it — flashka-web AND flashka-app's own DOM surfaces. It is not a
 * `shrink-0` utility in this string, and that is load-bearing: Tailwind emits
 * `flex-shrink` after `flex`, so a `shrink-0` here would beat a call site's
 * `flex-1` and break every truncating row and every scroller. Base < utilities
 * means `flex-1` / `shrink` / `basis-*` at a call site still win — only the
 * DEFAULT changes. NB `min-w-0` alone no longer implies shrinking: pair it with
 * `shrink` where a cluster is meant to truncate.
 *
 * `position: relative` is deliberately NOT part of the reset even though RNW has
 * it: it would move the containing block for every absolutely positioned
 * descendant on web. Tracked separately.
 */
const RESET_CLASSES = 'fk-view flex flex-col items-stretch box-border min-w-0 min-h-0';

export function View({
  as = 'div',
  className,
  children,
  testID,
  accessibilityLabel,
  accessibilityRole,
  webProps,
  nativeProps: _nativeProps,
}: ViewProps) {
  return createElement(
    as,
    {
      ...webProps,
      className: cn(RESET_CLASSES, className),
      'aria-label': accessibilityLabel ?? webProps?.['aria-label'],
      role: accessibilityRole ?? webProps?.role,
      'data-testid': testID,
    },
    children,
  );
}
